import React from 'react';
import { Helmet } from 'react-helmet';
import DetailsHeaderText from '../components/DetailsHeaderText';
import rideImage3 from '../assets/images/ride/ride_3.png';
import rideImage4 from '../assets/images/ride/ride_4.png';
import rideImage5 from '../assets/images/ride/ride_5.png';
import rideImage6 from '../assets/images/ride/ride_6.png';
import rideImage7 from '../assets/images/ride/ride_7.png';
import rideImage8 from '../assets/images/ride/ride_8.png';
import rideImage9 from '../assets/images/ride/ride_9.png';
import rideImage10 from '../assets/images/ride/ride_10.png';
import rideImage11 from '../assets/images/ride/ride_11.png';
import rideImage12 from '../assets/images/ride/ride_12.png';
import rideImage13 from '../assets/images/ride/ride_13.png';
import journeyPlanningRefImage from '../assets/images/ride/journey_planning_ref.png';
import publicTransportInfoRefImage from '../assets/images/ride/public_transport_info_ref.png';
import planFromVehicleRefImage from '../assets/images/ride/plan_from_vehicle_ref.png';
import journeyPlanning from '../assets/videos/journey_planning.mp4';
import publicTransportInfo from '../assets/videos/public_transport_info.mp4';
import planFromVehicle from '../assets/videos/plan_from_vehicle.mp4';
import DetailsText from '../components/DetailsText';
import TextWithImageSection from '../components/TextWithImageSection';
import Subtitle from '../components/Subtitle';
import LeadText from '../components/LeadText';
import Text from '../components/Text';
import SectionDetailsLink from '../components/SectionDetailsLink';
import { sectionIds } from './utils';
import Media from '../components/Media';
import SectionTitle from '../components/SectionTitle';
import Paragraph from '../components/Paragraph';
import DividerTitle from '../components/DividerTitle';
import Title from '../components/Title';
import DetailsList from '../components/DetailsList';
import PoweringImageContainer from '../components/PoweringImageContainer';
import ImageWithLogo from '../components/ImageWithLogo';
import SideVideo from '../components/SideVideo';
import HighlightedText from '../components/HighlightedText';
import PageFooterSlope from './PageFooterSlope';

const RideCardDetailsContent = () => (
    <>
        <Helmet>
            <title>realCity ride - passenger information & trip planning</title>
            <meta
                name="description"
                content="realCity | ride is a mobile and web-based trip planning solution. Get real-time public transport status, bike-sharing availability, and multi-modal travel options."
            />
            <meta
                name="keywords"
                content="passenger information and trip planning solution, real-time public transport tracking, mobile trip planner app, multi-modal journey planning, web-based route planner, real-time travel assistant"
            />
        </Helmet>
        <DetailsHeaderText
            category="Products"
            title="realCity | ride"
        />
        <TextWithImageSection
            introductorySection
            followedByDetailsText
            text={(
                <>
                    <Subtitle>Real-time journey planning & passenger information system</Subtitle>
                    <Text>
                        <LeadText>
                            Can you imagine passengers always knowing when their ride is coming, transit operators
                            getting fewer complaints and every point of interest is visible at a glance? With realCity |
                            ride it can be a reality.
                        </LeadText>
                        Get to know our fully customizable, real-time journey planning platform and passenger
                        information system!
                    </Text>
                    <SectionDetailsLink navigateTo={`/#${sectionIds.contactId}`}>
                        Lets connect!
                    </SectionDetailsLink>
                </>
            )}
            image={(
                <Media
                    component="img"
                    src={rideImage3}
                    variant="left"
                    alt="Ride & Real-time journey planning & passenger information system"
                />
            )}
            alignment="image-right"
        />
        <DetailsText withBottomMargin>
            <SectionTitle>
                A journey planner that makes public transport the logical choice
            </SectionTitle>
            <Paragraph>
                In today’s growing cities, getting around isn’t always simple. Crowded streets, unpredictable traffic
                jams and schedule changes can turn even short trips into stressful experiences. But it doesn’t have to
                be this way.
            </Paragraph>
            <Paragraph>
                Modern passengers expect live information at their fingertips, reliable transfers, and the ability to
                mix and match between buses, trams, bikes, and walking in order to get to their destination sooner.
                realCity | ride provides exactly this!
            </Paragraph>
            <Paragraph>
                It supports complete, door-to-door journey planning, letting users switch between transport modes, get
                real-time updates, and see all their points of interests nearby, all from a single interface.
            </Paragraph>
        </DetailsText>
        <DividerTitle>realCity | ride - Real-time passenger info in your pocket</DividerTitle>
        <TextWithImageSection
            text={(
                <>
                    <Title>Multimodal, door-to-door journey planning</Title>
                    <Text>
                        realCity | ride combines public transport, walking, and cycling in one simple journey planner.
                        Passengers can quickly compare all available route options at a glance, choose the fastest
                        route, the fewest transfers, or the most convenient connection. They can even choose to cover
                        part of the route using their own bike or a shared bicycle service.
                    </Text>
                </>
            )}
            image={(
                <SideVideo
                    variant="right"
                    poster={journeyPlanningRefImage}
                    alt="Ride & Multimodal, door-to-door journey planning"
                >
                    <source src={journeyPlanning} type="video/mp4" />
                </SideVideo>
            )}
            alignment="image-left"
            first
        />
        <TextWithImageSection
            text={(
                <>
                    <Title>Real-time public transport information</Title>
                    <Text>
                        In realCity | ride, a live maps shows the real-time position of approaching vehicles, so
                        passengers can see at a glance if they are making it to the stop or not. They can also see if
                        their ride is delayed, nearby, or already arriving and adjust their timing accordingly. That
                        means no more unnecessary rushing.
                    </Text>
                </>
            )}
            image={(
                <SideVideo
                    variant="left"
                    poster={publicTransportInfoRefImage}
                    alt="Ride & Real-time public transport information"
                >
                    <source src={publicTransportInfo} type="video/mp4" />
                </SideVideo>
            )}
            alignment="image-right"
        />
        <TextWithImageSection
            text={(
                <>
                    <Title>Plan directly from the vehicle</Title>
                    <Text>
                        Passengers don’t always have time to plan their entire trip before hopping on a bus or tram.
                        With the plan from vehicle function, they don’t need to. Our algorithm automatically identifies
                        the vehicle a passenger is traveling on by using location data, nearby vehicles and direction of
                        movement to calculate the most efficient route.
                    </Text>
                </>
            )}
            image={(
                <SideVideo
                    variant="right"
                    poster={planFromVehicleRefImage}
                    alt="Ride & Plan directly from the vehicle"
                >
                    <source src={planFromVehicle} type="video/mp4" />
                </SideVideo>
            )}
            alignment="image-left"
        />
        <TextWithImageSection
            text={(
                <>
                    <Title>Modern web application</Title>
                    <Text>
                        In addition to our native iOS and Android apps, we also offer a modern web application that runs
                        directly in any browser. This makes the platform more accessible to users of all ages and
                        technical backgrounds. For many passengers the web interface offers a convenient way to plan
                        their journey in advance from a larger screen at home.
                    </Text>
                </>
            )}
            image={(
                <Media
                    component="img"
                    src={rideImage4}
                    variant="left"
                    alt="Ride & Modern web application"
                />
            )}
            alignment="image-right"
        />
        <TextWithImageSection
            text={(
                <>
                    <Title>Targeted alerts through push notifications</Title>
                    <Text>
                        Passengers can receive alerts related to their favorite stops or routes within the app. When the
                        operator registers an alert, it not only appears in the application, but is also delivered
                        directly to the subscribed users as a push notification. Operators can also send network-wide
                        notifications to all users about major incidents affecting the entire public transport system.
                        This ensures passengers receive only relevant information without needing to actively check
                        schedules or apps throughout the day.
                    </Text>
                </>
            )}
            image={(
                <Media
                    component="img"
                    src={rideImage13}
                    variant="right"
                    alt="Ride & Unique ways to display nearby departures"
                />
            )}
            alignment="image-left"
        />
        <TextWithImageSection
            text={(
                <>
                    <Title>Unique ways to display nearby departures</Title>
                    <Text>
                        We’re not just developers, we are also users of our own apps. That’s how the idea was born to
                        display live departure data in high traffic areas. The first screen was installed in our office
                        building, showing which vehicles are arriving nearby and how long it takes to walk to the stop.
                        These displays can be placed in lobbies, elevators, or ticket machines, making public transport
                        even more convenient.
                    </Text>
                </>
            )}
            image={(
                <Media
                    component="img"
                    src={rideImage5}
                    variant="left"
                    alt="Ride & Unique ways to display nearby departures"
                />
            )}
            alignment="image-right"
            last
        />
        <DividerTitle>Better service, increased trust, more passengers</DividerTitle>
        <DetailsText>
            <SectionTitle>So, what’s in it for you?</SectionTitle>
            <LeadText>You have full control over how the app works.</LeadText>
            We provide a user friendly backoffice platform with a graphical interface that puts operators in full
            control.
        </DetailsText>
        <TextWithImageSection
            text={(
                <Text>
                    <DetailsList>
                        <li>
                            <HighlightedText>Update points of interest in seconds</HighlightedText>
                            &nbsp;from ticket machines to public amenities or micromobility options. Each POI can carry
                            useful extra details like opening hours or the real-time number of available bikes at a
                            docking station, helping passengers make smarter choices on the go.
                        </li>
                        <li>
                            <HighlightedText>Operators can instantly create and publish alerts</HighlightedText>
                            &nbsp;for a specific line, stop, or even a single trip, thanks to the built-in alert
                            manager. Passengers then see clear warnings right inside the trip planner, and can also
                            receive push notifications if enabled.
                        </li>
                        <li>
                            <HighlightedText>That means more efficient workflow and more satisfied riders</HighlightedText>
                            &nbsp;through improved service planning, higher trust in the system and fewer passenger
                            complaints.
                        </li>
                    </DetailsList>
                </Text>
            )}
            image={(
                <Media
                    component="img"
                    src={rideImage6}
                    variant="left"
                    alt="Ride & So, what’s in it for you?"
                />
            )}
            alignment="image-right"
        />
        <DetailsText>
            <Paragraph>
                Thanks to our cloud-based infrastructure, realCity | ride scales effortlessly. It doesn’t matter how big
                a fleet you operate, you stay in control while the system adapts to your needs.
            </Paragraph>
            <Paragraph>
                Use it as a standalone passenger information tool, or integrate it fully into the broader realCity
                platform via the realCity core backend and API server including modules like dispatch, drive, and
                replay.
            </Paragraph>
            <SectionTitle>They are already using our application</SectionTitle>
            <PoweringImageContainer>
                <ImageWithLogo src={rideImage7} logo="bkk" />
                <ImageWithLogo src={rideImage8} logo="szombathely" />
                <ImageWithLogo src={rideImage9} logo="tbusz" />
                <ImageWithLogo src={rideImage10} logo="vbusz" />
                <ImageWithLogo src={rideImage11} logo="thurybusz" />
                <ImageWithLogo src={rideImage12} logo="kti" />
            </PoweringImageContainer>
            <Paragraph>And a continuously growing network of transport operators across Europe.</Paragraph>
        </DetailsText>
        <DetailsText>
            <SectionTitle>Ready to upgrade your city’s passenger experience?</SectionTitle>
            <Paragraph>
                Book a personalized walkthrough with our mobility experts and discover how realCity | ride can help you
                create smarter, more connected journeys powered by your real-time data.
            </Paragraph>
            <SectionDetailsLink navigateTo={`/#${sectionIds.contactId}`}>
                Lets connect!
            </SectionDetailsLink>
        </DetailsText>
        <PageFooterSlope />
    </>
);

export default RideCardDetailsContent;
